import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { PanelLayout } from './components/PanelLayout';
import { LoadingRegion } from './components/Spinner';

// Public leaderboard is the unauthenticated index: load it eagerly-ish but keep
// the heavy panel pages (forms, charts) split out so first paint stays lean.
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Questions = lazy(() => import('./pages/Questions'));
const QuestionEditor = lazy(() => import('./pages/QuestionEditor'));
const Submissions = lazy(() => import('./pages/Submissions'));
const SubmissionDetail = lazy(() => import('./pages/SubmissionDetail'));
const StudentProfile = lazy(() => import('./pages/StudentProfile'));
const Settings = lazy(() => import('./pages/Settings'));
const Admin = lazy(() => import('./pages/Admin'));
const NotFound = lazy(() => import('./pages/NotFound'));

/** Gate panel routes behind a Firebase session. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, configured } = useAuth();
  if (!configured) return <Navigate to="/login" replace />;
  if (loading) return <FullPageLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Bounce an already-signed-in teacher away from the login screen. */
function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoading />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function FullPageLoading() {
  return (
    <div className="flex min-h-full items-center justify-center">
      <LoadingRegion label="Loading" />
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<FullPageLoading />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Navigate to="/leaderboard" replace />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route
              path="/login"
              element={
                <RedirectIfAuthed>
                  <Login />
                </RedirectIfAuthed>
              }
            />

            {/* Authed panel */}
            <Route
              element={
                <RequireAuth>
                  <PanelLayout />
                </RequireAuth>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/questions" element={<Questions />} />
              <Route path="/questions/new" element={<QuestionEditor />} />
              <Route path="/questions/:id/edit" element={<QuestionEditor />} />
              <Route path="/submissions" element={<Submissions />} />
              <Route path="/submissions/:id" element={<SubmissionDetail />} />
              <Route path="/students/:userId" element={<StudentProfile />} />
              <Route path="/board" element={<Leaderboard embedded />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/admin" element={<Admin />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
